import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, yen } from "../lib/api";
import { useI18n, catLabel } from "../lib/i18n";
import { StorefrontHeader } from "../components/StorefrontHeader";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { ProductRow } from "../components/ProductRow";
import { ShareProductButton } from "../components/ShareProductButton";
import { Button } from "../components/ui/button";
import { ArrowLeft, ShoppingBag } from "lucide-react";

export default function ProdukDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [photo, setPhoto] = useState(0);

  useEffect(() => {
    setPhoto(0);
  }, [id]);

  useEffect(() => {
    setNotFound(false);
    setP(null);
    api
      .get(`/products/${id}`)
      .then(({ data }) => {
        setP(data);
        api
          .get("/products", { params: { kind: data.kind } })
          .then(({ data: all }) =>
            setRelated(all.filter((x) => x.id !== data.id && x.category === data.category).slice(0, 10))
          )
          .catch(() => setRelated([]));
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="min-h-screen bg-background">
        <StorefrontHeader />
        <div
          data-testid="product-not-found"
          className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-16 text-center"
        >
          <p className="text-lg font-semibold">{t("product_not_found")}</p>
          <Link
            to="/toko"
            data-testid="product-not-found-back"
            className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary hover:underline"
          >
            <ArrowLeft size={15} /> {t("back_to_shop")}
          </Link>
        </div>
      </div>
    );

  if (!p)
    return (
      <div className="min-h-screen bg-background">
        <StorefrontHeader />
        <p className="p-8 text-muted-foreground">{t("loading")}</p>
      </div>
    );

  const page = p.kind === "menu" ? "/resto" : "/toko";
  const desc = (lang === "ja" && p.description_ja) || p.description;
  const name = (lang === "ja" && p.name_ja) || p.name;
  const out = p.track_stock && p.stock <= 0;
  const photos = p.images?.length ? p.images : p.image_url ? [p.image_url] : [];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <StorefrontHeader active={p.kind === "menu" ? "resto" : "toko"} />
      <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <Link
          to={page}
          data-testid="product-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={15} /> {t("back_to_shop")}
        </Link>

        <div className="grid md:grid-cols-2 gap-8 mt-5">
          <div className="space-y-3">
            <div className="relative rounded-3xl overflow-hidden bg-gray-100 aspect-square">
              {photos.length > 0 ? (
                <img
                  src={photos[Math.min(photo, photos.length - 1)]}
                  alt={name}
                  data-testid="product-detail-photo"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full grid place-items-center text-gray-300">{p.category}</div>
              )}
              {p.is_new && (
                <span className="absolute top-4 left-4 rounded-full bg-primary text-white text-xs font-bold px-3 py-1.5">
                  NEW
                </span>
              )}
              <ShareProductButton product={p} testid="product-detail-share" />
            </div>
            {photos.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-1" data-testid="product-detail-thumbs">
                {photos.map((url, i) => (
                  <button
                    key={url}
                    data-testid={`product-detail-thumb-${i}`}
                    onClick={() => setPhoto(i)}
                    className={`h-20 w-20 shrink-0 rounded-2xl overflow-hidden border-2 transition-colors duration-200 ${
                      i === photo ? "border-primary" : "border-transparent hover:border-gray-300"
                    }`}
                  >
                    <img src={url} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="min-w-0">
            <p className="text-sm text-primary font-medium" data-testid="product-detail-category">
              {catLabel(p.category, lang)}
            </p>
            <h1
              className="font-display text-3xl sm:text-4xl font-semibold mt-1 leading-tight"
              data-testid="product-detail-name"
            >
              {name}
            </h1>
            <p className="num text-3xl font-semibold mt-4" data-testid="product-detail-price">
              {yen(p.price)}
              <span className="text-sm text-muted-foreground font-normal ml-2">/ {p.unit}</span>
            </p>
            {p.weight && <p className="text-sm text-muted-foreground mt-1">{p.weight}</p>}
            {desc && (
              <p className="text-base leading-relaxed mt-5" data-testid="product-detail-desc">
                {desc}
              </p>
            )}
            <p className="text-sm mt-5" data-testid="product-detail-stock">
              {out ? (
                <span className="text-red-600 font-medium">{t("out_of_stock")}</span>
              ) : (
                <span className="text-emerald-600 font-medium">{t("in_stock")}</span>
              )}
            </p>
            <Button
              className="w-full sm:w-auto mt-6"
              size="lg"
              disabled={out}
              data-testid="product-detail-add-btn"
              onClick={() => nav(`${page}?add=${p.id}`)}
            >
              <ShoppingBag size={17} className="mr-2" /> {t("add_to_cart")}
            </Button>
          </div>
        </div>

        <div className="mt-12">
          <ProductRow
            title={t("related_products")}
            products={related}
            onPick={(x) => nav(`/produk/${x.id}`)}
            prefix="related"
          />
        </div>
      </div>
      <WhatsAppFloat />
    </div>
  );
}
